import React, { createContext, useContext, useMemo, useRef } from "react";

/**
 * Annotation-editor registry.
 *
 * Bridges two sibling React subtrees that share no Lexical context: the
 * rich-text editors (which own the annotation create/edit/remove logic) and a
 * host UI elsewhere on the page that needs to drive those operations without
 * an editor in scope — e.g. PublishedPage's sidebar AnnoCards.
 *
 * Mount <AnnoEditorRegistryProvider> at the common ancestor of both. Each
 * field's AnnoPlugin registers an imperative handle exposing its EXISTING
 * handlers (beginEdit→acquireLock, confirmEdit→handleSetAnnoData,
 * remove→handleRemove, etc.). A host looks the handle up by annoDataId and
 * calls those — so nothing about annotation mutation is reimplemented outside
 * the editor.
 *
 * The context value is a STABLE imperative api (frozen by useMemo) backed by a
 * ref-held Map — registering/unregistering must never re-render consumers
 * (editors register on every markNodeMap change). Consumers read it at
 * action-time (a click handler), not during render. Same pattern Lexical's own
 * LexicalComposerContext uses for the editor instance.
 */
const AnnoEditorRegistryContext = createContext(null);

export const AnnoEditorRegistryProvider = ({ children }) => {
  const registryRef = useRef(new Map()); // editor instance -> handle

  const api = useMemo(
    () => ({
      register: (editor, handle) => {
        registryRef.current.set(editor, handle);
        return () => registryRef.current.delete(editor);
      },
      /** Find the handle of whichever registered editor hosts this annoDataId. */
      find: (annoDataId) => {
        for (const handle of registryRef.current.values()) {
          if (handle.hostsDataId(annoDataId)) return handle;
        }
        return null;
      },
      /**
       * Broadcast the full set of filtered-out (dimmed) annoDataIds to every
       * registered editor, each of which repaints its own marks ONCE. The host
       * computes the complete set and calls this a single time per filter change
       * — replacing the old per-annotation dimMark loop that cost O(annos ×
       * fragments) and flickered through partial states. Each editor checks
       * membership against the full set for its own fragments, so passing one
       * shared (read-only) Set to all of them is correct.
       */
      setDimmedAnnos: (dimmedIdSet, color) => {
        for (const handle of registryRef.current.values()) {
          handle.setDimmedAnnos?.(dimmedIdSet, color);
        }
      },
    }),
    []
  );

  return (
    <AnnoEditorRegistryContext.Provider value={api}>
      {children}
    </AnnoEditorRegistryContext.Provider>
  );
};

/**
 * Returns the registry api ({ register, find }) or null when no provider is
 * mounted (e.g. legacy edit pages). Callers must handle null.
 */
export const useAnnoEditorRegistry = () => useContext(AnnoEditorRegistryContext);
